import { TypedDocumentNode, gql } from '@apollo/client';

import { withFragments } from '@sorare/core/src/lib/gql';

import bundesligaPlayers from 'assets/new-season/bundesliga_players.png';
import laligaPlayers from 'assets/new-season/laliga_players.png';
import ligue1Players from 'assets/new-season/ligue_1_players.png';
import premierLeaguePlayers from 'assets/new-season/premier_league_players.png';

import type {
  getLeagueAvatar_competition,
  getLeagueAvatar_so5Competition,
  getLeagueBackgroundUrl_competition,
  getLeagueBackgroundUrl_so5Competition,
} from './__generated__/newSeason.graphql';

const july17 = new Date('2025-07-17T14:00:00+02:00');
const july21 = new Date('2025-07-21T14:00:00+02:00');
const july22 = new Date('2025-07-22T14:00:00+02:00');
const july23 = new Date('2025-07-23T14:00:00+02:00');
const july24 = new Date('2025-07-24T14:00:00+02:00');
const july28 = new Date('2025-07-28T14:00:00+02:00');
const july29 = new Date('2025-07-29T14:00:00+02:00');
const july31 = new Date('2025-07-31T14:00:00+02:00');
const august4 = new Date('2025-08-04T14:00:00+02:00');
const august7 = new Date('2025-08-07T14:00:00+02:00');
const august12 = new Date('2025-08-12T14:00:00+02:00');

export enum LaunchType {
  MAIN_LEAGUE = 'MAIN_LEAGUE',
  SECONDARY_LEAGUE = 'SECONDARY_LEAGUE',
}

export type LeagueLaunch<T = undefined, C = unknown> = {
  slug: string;
  name: string;
  startDate: Date;
  competition?: T;
  launchType: LaunchType;
  playersUrl?: string;
  so5Competition?: C;
};

export const LEAGUE_LAUNCHES: LeagueLaunch<undefined>[] = [
  {
    slug: 'jupiler-pro-league',
    name: 'Jupiler Pro League',
    startDate: july17,
    launchType: LaunchType.SECONDARY_LEAGUE,
  },
  {
    slug: 'premiership-gb-sct',
    name: 'Premiership',
    startDate: july21,
    launchType: LaunchType.SECONDARY_LEAGUE,
  },
  {
    slug: 'superliga-dk',
    name: 'Superliga',
    startDate: july22,
    launchType: LaunchType.SECONDARY_LEAGUE,
  },
  {
    slug: 'austrian-bundesliga',
    name: 'Austrian Bundesliga',
    startDate: july23,
    launchType: LaunchType.SECONDARY_LEAGUE,
  },
  {
    slug: '1-hnl',
    name: 'SuperSport HNL',
    startDate: july24,
    launchType: LaunchType.SECONDARY_LEAGUE,
  },
  {
    slug: '2-bundesliga',
    name: '2. Bundesliga',
    startDate: july24,
    launchType: LaunchType.SECONDARY_LEAGUE,
  },
  {
    slug: 'ligue-2-fr',
    name: 'Ligue 2',
    startDate: july24,
    launchType: LaunchType.SECONDARY_LEAGUE,
  },
  {
    slug: 'primeira-liga-pt',
    name: 'Primeira Liga',
    startDate: july28,
    launchType: LaunchType.SECONDARY_LEAGUE,
  },
  {
    slug: 'spor-toto-super-lig',
    name: 'Süper Lig',
    startDate: july28,
    launchType: LaunchType.SECONDARY_LEAGUE,
  },
  {
    slug: 'eredivisie',
    name: 'Eredivisie',
    startDate: july29,
    launchType: LaunchType.SECONDARY_LEAGUE,
  },
  {
    slug: 'premier-league-gb-eng',
    name: 'Premier League',
    startDate: july31,
    launchType: LaunchType.MAIN_LEAGUE,
    playersUrl: premierLeaguePlayers,
  },
  {
    slug: 'ligue-1-fr',
    name: 'Ligue 1',
    startDate: august4,
    launchType: LaunchType.MAIN_LEAGUE,
    playersUrl: ligue1Players,
  },
  {
    slug: 'laliga-es',
    name: 'LaLiga EA Sports',
    startDate: august7,
    launchType: LaunchType.MAIN_LEAGUE,
    playersUrl: laligaPlayers,
  },
  {
    slug: 'bundesliga-de',
    name: 'Bundesliga',
    startDate: august12,
    launchType: LaunchType.MAIN_LEAGUE,
    playersUrl: bundesligaPlayers,
  },
];

export const getLeagueAvatar = withFragments(
  (
    league: {
      name?: string;
      slug?: string;
      so5Competition?: getLeagueAvatar_so5Competition;
      competition?: getLeagueAvatar_competition;
    },
    mode: 'icon' | 'logo' = 'logo'
  ) => {
    const { so5Competition, competition } = league;
    const isSo5Competition = so5Competition?.competitions.length === 1;

    if (isSo5Competition) {
      return {
        name: so5Competition.displayName,
        slug: so5Competition.slug,
        avatarUrl:
          mode === 'logo' ? so5Competition.logoUrl : so5Competition.iconUrl,
      };
    }
    return {
      name: competition?.name || league.name || '',
      slug: competition?.slug || league.slug || '',
      avatarUrl: competition?.pictureUrl,
    };
  },
  {
    competition: gql`
      fragment getLeagueAvatar_competition on Competition {
        slug
        name
        pictureUrl
      }
    ` as TypedDocumentNode<getLeagueAvatar_competition>,
    so5Competition: gql`
      fragment getLeagueAvatar_so5Competition on So5Competition {
        slug
        displayName
        logoUrl
        iconUrl
        competitions {
          slug
        }
      }
    ` as TypedDocumentNode<getLeagueAvatar_so5Competition>,
  }
);

export const getLeagueBackgroundUrl = withFragments(
  (league: {
    so5Competition?: getLeagueBackgroundUrl_so5Competition;
    competition?: getLeagueBackgroundUrl_competition;
  }) => {
    return (
      league.so5Competition?.headerBackgroundDesktopUrl ||
      league.competition?.backgroundPictureUrl
    );
  },
  {
    competition: gql`
      fragment getLeagueBackgroundUrl_competition on Competition {
        slug
        backgroundPictureUrl
      }
    ` as TypedDocumentNode<getLeagueBackgroundUrl_competition>,
    so5Competition: gql`
      fragment getLeagueBackgroundUrl_so5Competition on So5Competition {
        slug
        headerBackgroundDesktopUrl
        competitions {
          slug
        }
      }
    ` as TypedDocumentNode<getLeagueBackgroundUrl_so5Competition>,
  }
);
